import { useState } from "react";
import { ThumbsUp, ThumbsDown, Loader2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getSessionId } from "@/lib/sessionId";
import { toast } from "sonner";
import RetroConfetti from "./RetroConfetti";
import { z } from "zod";

interface Props {
  query: string;
}

const commentSchema = z
  .string()
  .trim()
  .min(1, { message: "Please tell us a bit about what went wrong." })
  .max(2000, { message: "Keep it under 2000 characters please." });

export default function SearchResultsFeedback({ query }: Props) {
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState<"up" | "down" | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showDownModal, setShowDownModal] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sendFeedback = async (rating: "up" | "down", commentText?: string) => {
    try {
      const { error } = await supabase.from("search_results_feedback").insert({
        query,
        rating,
        comment: commentText?.trim() || null,
        session_id: getSessionId(),
        user_id: user?.id ?? null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      });
      if (error) throw error;
      return true;
    } catch (err) {
      console.error("[SearchResultsFeedback] insert failed:", err);
      toast.error("Couldn't save your feedback. Try again?");
      return false;
    }
  };

  const handleUp = async () => {
    if (submitted) return;
    setSubmitted("up");
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 2200);
    const ok = await sendFeedback("up");
    if (!ok) setSubmitted(null);
  };

  const handleDown = () => {
    if (submitted) return;
    setShowDownModal(true);
  };

  const handleDownSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = commentSchema.safeParse(comment);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message || "Please add a few words.");
      return;
    }
    setSubmitting(true);
    const ok = await sendFeedback("down", parsed.data);
    setSubmitting(false);
    if (ok) {
      setSubmitted("down");
      setShowDownModal(false);
      toast.success("Thank you — feedback received. We'll use it to improve.");
    }
  };

  if (submitted === "up") {
    return (
      <>
        <RetroConfetti active={showConfetti} />
        <div className="max-w-2xl mx-auto mt-6 sm:mt-8">
          <div className="retro-border bg-card p-3 sm:p-4 text-center">
            <p className="font-mono text-xs sm:text-sm text-foreground">
              🎉 Thanks for the thumbs up! Glad these hit the mark.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (submitted === "down") {
    return (
      <div className="max-w-2xl mx-auto mt-6 sm:mt-8">
        <div className="retro-border bg-card p-3 sm:p-4 text-center">
          <p className="font-mono text-xs sm:text-sm text-foreground">
            🙏 Thanks for the honest feedback. We'll use it to improve our matches.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <RetroConfetti active={showConfetti} />
      <div className="max-w-2xl mx-auto mt-6 sm:mt-8 animate-fade-in">
        <div className="retro-border bg-card p-3 sm:p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-xs sm:text-sm text-foreground text-center sm:text-left">
            How are these results?
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleUp}
              aria-label="These results are good"
              className="btn-comic-secondary btn-comic-sm gap-1.5 hover:bg-confidence-high hover:text-white hover:border-confidence-high"
            >
              <ThumbsUp className="w-4 h-4" />
              <span>Good</span>
            </button>
            <button
              onClick={handleDown}
              aria-label="These results need improvement"
              className="btn-comic-secondary btn-comic-sm gap-1.5 hover:bg-[hsl(8,72%,46%)] hover:text-white hover:border-[hsl(8,70%,28%)]"
            >
              <ThumbsDown className="w-4 h-4" />
              <span>Not great</span>
            </button>
          </div>
        </div>
      </div>

      {showDownModal && (
        <div
          className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => !submitting && setShowDownModal(false)}
        >
          <div
            className="relative w-full max-w-lg modal-shell animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !submitting && setShowDownModal(false)}
              className="absolute top-3 right-3 p-1 hover:bg-muted rounded-md z-10"
              aria-label="Close"
              disabled={submitting}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="modal-shell-header">
              <h3 className="modal-shell-title">We're so sorry! 😔</h3>
              <p className="modal-shell-desc">
                Can you tell us why these results weren't good so we can improve?
              </p>
            </div>

            <form onSubmit={handleDownSubmit}>
              <div className="modal-shell-body">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="What were you hoping to find? What was off about these episodes?"
                  rows={4}
                  maxLength={2000}
                  autoFocus
                  disabled={submitting}
                  className="textarea-retro disabled:opacity-50"
                />
                <p className="font-mono text-[10px] text-muted-foreground mt-2 text-right">
                  {comment.length}/2000
                </p>
              </div>
              <div className="modal-shell-footer">
                <button
                  type="button"
                  onClick={() => setShowDownModal(false)}
                  disabled={submitting}
                  className="btn-comic-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || comment.trim().length === 0}
                  className="btn-comic-primary disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Send feedback
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
